import React from 'react';
import { useBemCN } from '@gik/core/utils/bemBlock';
import moment from 'moment';
import DateTimeUtils from '@gik/core/utils/DateTimeUtils';
import { AnimatedDivBone, LineClamp } from '@gik/ui';
import InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import AddInkindUserUpdateButton from '@gik/inkind-page/components/AddInkindUserUpdateButton/AddInkindUserUpdateButton';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';

export interface IInkindUserUpdateCardProps {
  update?: InkindUserUpdate;
  isOrganiser: boolean;
  isLoading?: boolean;
  isPlaceholder?: boolean;
  showAddButton?: boolean;
  addButtonIndex?: number;
  isLoggedIn?: boolean;
  hasUpdates?: boolean;

  openAddUpdateModal(): void;

  openTimelineModal?(update: InkindUserUpdate): void;
}

const blockName = 'inkind-user-update-card';

export default function InkindUserUpdateCard({
  update,
  isOrganiser,
  isPlaceholder,
  isLoading,
  showAddButton,
  addButtonIndex,
  isLoggedIn,
  hasUpdates,
  openAddUpdateModal,
  openTimelineModal,
}: IInkindUserUpdateCardProps) {
  const bem = useBemCN(blockName);
  const { t } = useTranslation();

  const showUserPlaceholder = isLoggedIn && !isOrganiser && !isLoading && isPlaceholder;
  const primaryMedia = update?.media.find(m => m.type == 'photo') || update?.media.find(m => m.type == 'video');

  const isOpenable = Boolean(openTimelineModal);

  const addButton = <AddInkindUserUpdateButton iconOnly={addButtonIndex > 0} onClick={() => openAddUpdateModal?.()} />;

  const userPlaceholder = (
    <>
      <p {...bem('no-update-title')}>
        {t(
          hasUpdates
            ? translationKeys.InkindUserUpdateCardPlaceholderCopy
            : translationKeys.InkindUserUpdateCardPlaceholderCopyNoUpdates
        )}
      </p>
      {!hasUpdates && <p {...bem('subtitle')}>{t(translationKeys.InkindUserUpdateCardPlaceholderSubtitleCopy)}</p>}
    </>
  );

  const content = (
    <div {...bem('content')}>
      {update && (
        <>
          <div {...bem('thumbnail-wrapper')}>
            <AnimatedDivBone {...bem('card-thumbnail-skeleton')} />
            <img alt="thumbnail" {...bem('card-thumbnail')} src={primaryMedia?.thumbnailUrl} />
          </div>
          <div {...bem('text-wrapper')}>
            <p {...bem('date')}>{moment(update.publishedDate, DateTimeUtils.DateFormat).format('MMM DD, YYYY')}</p>
            <div {...bem('title')}>
              <LineClamp lines={2} fadeOut={false} showButton={false}>
                <p>{update.title}</p>
              </LineClamp>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      {...bem(null, [
        { 'no-thumbnail': !primaryMedia?.thumbnailUrl },
        { placeholder: isPlaceholder },
        { 'logged-in': showUserPlaceholder },
        { 'add-button': showAddButton },
      ])}
      onClick={() => isOpenable && openTimelineModal(update)}
    >
      {showAddButton || showUserPlaceholder ? (
        <div {...bem('no-updates')}>{showAddButton ? addButton : userPlaceholder}</div>
      ) : (
        content
      )}
    </div>
  );
}
